import { Loader2, Mail, Search, Settings } from "lucide-react";
import { Fragment, useState } from "react";
import { useParams } from "react-router";

import { PageHeader, ProvenanceBadge } from "@/components/comms";
import { EVENT_LABELS, STEP_EVENT_TYPES, useOrderingCustomer } from "@/lib/comms";
import { cn } from "@/lib/utils";

import { EmailLogDrawer } from "./email-log-drawer";
import { PreferencesDrawer } from "./preferences-drawer";

export function OrderingDetailPage() {
  const { accountId } = useParams();
  const customerQuery = useOrderingCustomer(accountId ?? null);

  const [orderSearch, setOrderSearch] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const detail = customerQuery.data;
  const term = orderSearch.trim();
  const orders = (detail?.orders ?? []).filter((o) => String(o.number).includes(term));
  // Derive from live query data so the drawer sub + steppers refresh after events.
  const selectedOrder = detail?.orders.find((o) => o.id === selectedOrderId) ?? null;

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          backTo="/ordering"
          backLabel="Back to customer list"
          title="Ordering Event Proactive Comms"
        />
        <div className="flex shrink-0 items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => setPrefsOpen(true)}
            title="Customer notification preferences"
            aria-label="Customer notification preferences"
            className="border-wg-03 text-wg-07 hover:text-jj-red flex size-9 cursor-pointer items-center justify-center rounded-full border bg-white"
          >
            <Settings className="size-[18px]" />
          </button>
        </div>
      </div>

      {customerQuery.isLoading ? (
        <div className="py-16">
          <Loader2 className="text-wg-05 mx-auto size-6 animate-spin" />
        </div>
      ) : customerQuery.isError || !detail ? (
        <p className="text-wg-06 py-16 text-center">Customer not found.</p>
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <div className="border-wg-03 flex w-[260px] items-center gap-2 rounded-full border bg-white px-3.5 py-2">
              <Search className="text-wg-06 size-4 shrink-0" />
              <input
                type="text"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Search order number"
                autoComplete="off"
                className="placeholder:text-wg-05 w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>

          <div className="border-wg-03 mb-6 grid grid-cols-[1fr_auto_auto] items-center gap-10 rounded-2xl border bg-white px-6 py-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-wg-06 text-[11px] font-semibold tracking-[0.08em] uppercase">
                  Account
                </span>
                <ProvenanceBadge
                  provenance={detail.summary.provenance}
                  className="text-[8px] opacity-60"
                />
              </div>
              <div className="font-heading text-wg-08 mt-1 text-[22px] font-medium">
                {detail.summary.name}
              </div>
            </div>
            <div>
              <div className="text-wg-06 text-[11px] font-semibold tracking-[0.08em] uppercase">
                Orders in progress
              </div>
              <div className="font-heading text-wg-08 mt-1 text-[22px] font-medium tabular-nums">
                {detail.summary.orders_in_progress}
              </div>
            </div>
            <div>
              <div className="text-wg-06 text-[11px] font-semibold tracking-[0.08em] uppercase">
                Total emails sent
              </div>
              <div className="font-heading text-wg-08 mt-1 text-[22px] font-medium tabular-nums">
                {detail.summary.emails_sent}
              </div>
            </div>
          </div>

          <div className="text-wg-07 grid grid-cols-[150px_1fr_auto] gap-5 px-4 pb-2 text-xs font-semibold">
            <span>Order number</span>
            <span>Order progression</span>
            <span>Emails</span>
          </div>

          {orders.length === 0 ? (
            <p className="text-wg-06 py-10 text-center text-sm">No orders match “{term}”.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {orders.map((o) => (
                <div
                  key={o.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedOrderId(o.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedOrderId(o.id);
                    }
                  }}
                  className={cn(
                    "grid cursor-pointer grid-cols-[150px_1fr_auto] items-center gap-5 rounded-2xl border bg-white px-4 py-3",
                    selectedOrderId === o.id ? "border-jj-red" : "border-wg-03 hover:border-wg-05",
                  )}
                >
                  <span className="flex flex-col items-start gap-0.5">
                    <ProvenanceBadge provenance={o.provenance} className="text-[8px] opacity-60" />
                    <span className="bg-wg-02 text-wg-08 inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-[13px] font-semibold tabular-nums">
                      #{o.number}
                    </span>
                  </span>
                  <OrderStepper reached={o.reached} backordered={o.backordered} />
                  <span className="border-wg-03 text-wg-07 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] font-semibold tabular-nums">
                    <Mail className="size-3.5" />
                    {o.email_count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <EmailLogDrawer
        order={selectedOrder}
        customerName={detail?.summary.name ?? ""}
        onClose={() => setSelectedOrderId(null)}
      />
      {accountId && detail && (
        <PreferencesDrawer
          customerId={accountId}
          customerName={detail.summary.name}
          open={prefsOpen}
          onClose={() => setPrefsOpen(false)}
        />
      )}
    </div>
  );
}

// ── Lifecycle stepper (port of the mockup's stepperHTML) ─────────────────

type StepState = "done" | "todo" | "backorder";

interface StepNodeProps {
  label: string;
  state: StepState;
}

function StepNode({ label, state }: StepNodeProps) {
  return (
    <div className="relative flex shrink-0 flex-col items-center">
      <div
        className={cn(
          "size-3.5 rounded-full border-2",
          state === "done" && "border-wg-08 bg-wg-08",
          state === "todo" && "border-wg-04 bg-white",
          state === "backorder" && "border-risk bg-risk",
        )}
      />
      <div
        className={cn(
          "absolute top-full left-1/2 mt-1.5 -translate-x-1/2 text-[10px] leading-none whitespace-nowrap",
          state === "backorder" ? "text-risk font-semibold" : "text-wg-06",
        )}
      >
        {label}
      </div>
    </div>
  );
}

interface OrderStepperProps {
  /** How many of the six lifecycle steps (0-6) the order has reached. */
  reached: number;
  backordered: boolean;
}

function OrderStepper({ reached, backordered }: OrderStepperProps) {
  return (
    <div className="flex items-center pt-1.5 pb-[22px]">
      {STEP_EVENT_TYPES.map((step, i) => (
        <Fragment key={step}>
          {i > 0 && (
            <div className={cn("h-0.5 min-w-3 flex-1", i < reached ? "bg-wg-08" : "bg-wg-03")} />
          )}
          <StepNode label={EVENT_LABELS[step]} state={i < reached ? "done" : "todo"} />
        </Fragment>
      ))}
      {/* Spacer segment before the backorder branch (mockup .seg.spacer). */}
      <div className="min-w-6 flex-1" />
      <StepNode label={EVENT_LABELS.backorder} state={backordered ? "backorder" : "todo"} />
    </div>
  );
}
